/**
 * Device & runtime fingerprint. Everything here is observable from JS
 * and does not require permissions. Captured into every result payload
 * so a downstream reader can attribute a vector divergence to a specific
 * browser/GPU/colour-space combination without guesswork.
 */

export interface DeviceFingerprint {
  userAgent: string;
  platform: string;
  hardwareConcurrency: number | null;
  deviceMemoryGb: number | null;
  screen: {
    width: number;
    height: number;
    devicePixelRatio: number;
    orientation: string | null;
  };
  viewport: { width: number; height: number };
  webgl: {
    vendor: string | null;
    renderer: string | null;
    version: string | null;
    glslVersion: string | null;
  };
  colour: {
    /** Browser-reported colour gamut: "srgb" | "p3" | "rec2020" via @media. */
    gamut: string | null;
    /** Default canvas 2D colour space, when readable. */
    canvas2dColorSpace: string | null;
  };
  locale: string | null;
  timezone: string | null;
}

function readWebGLInfo(): DeviceFingerprint["webgl"] {
  const canvas = document.createElement("canvas");
  const gl =
    (canvas.getContext("webgl2") as WebGL2RenderingContext | null) ||
    (canvas.getContext("webgl") as WebGLRenderingContext | null) ||
    (canvas.getContext("experimental-webgl") as WebGLRenderingContext | null);
  if (!gl) {
    return { vendor: null, renderer: null, version: null, glslVersion: null };
  }
  // WEBGL_debug_renderer_info exposes the real GPU string. Chrome/Edge
  // expose it by default; Safari/Firefox redact it in some modes. When
  // redacted we still get the generic vendor/renderer below.
  const dbg = gl.getExtension("WEBGL_debug_renderer_info");
  const vendor = dbg
    ? (gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL) as string)
    : (gl.getParameter(gl.VENDOR) as string);
  const renderer = dbg
    ? (gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) as string)
    : (gl.getParameter(gl.RENDERER) as string);
  return {
    vendor: vendor ?? null,
    renderer: renderer ?? null,
    version: (gl.getParameter(gl.VERSION) as string) ?? null,
    glslVersion: (gl.getParameter(gl.SHADING_LANGUAGE_VERSION) as string) ?? null,
  };
}

function readColourGamut(): string | null {
  if (typeof window === "undefined" || !window.matchMedia) return null;
  for (const gamut of ["rec2020", "p3", "srgb"] as const) {
    if (window.matchMedia(`(color-gamut: ${gamut})`).matches) return gamut;
  }
  return null;
}

function readCanvasColorSpace(): string | null {
  try {
    const c = document.createElement("canvas");
    const ctx = c.getContext("2d");
    if (!ctx) return null;
    type CtxWithCS = CanvasRenderingContext2D & {
      getContextAttributes?: () => { colorSpace?: string };
    };
    const cs = (ctx as CtxWithCS).getContextAttributes?.().colorSpace;
    return cs ?? "srgb";
  } catch {
    return null;
  }
}

export function readDeviceFingerprint(): DeviceFingerprint {
  const nav = navigator as Navigator & { deviceMemory?: number };
  return {
    userAgent: nav.userAgent,
    platform: nav.platform,
    hardwareConcurrency: nav.hardwareConcurrency ?? null,
    deviceMemoryGb: typeof nav.deviceMemory === "number" ? nav.deviceMemory : null,
    screen: {
      width: window.screen?.width ?? 0,
      height: window.screen?.height ?? 0,
      devicePixelRatio: window.devicePixelRatio ?? 1,
      orientation: window.screen?.orientation?.type ?? null,
    },
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
    webgl: readWebGLInfo(),
    colour: {
      gamut: readColourGamut(),
      canvas2dColorSpace: readCanvasColorSpace(),
    },
    locale: Intl.DateTimeFormat().resolvedOptions().locale ?? null,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? null,
  };
}

/** Short, human-readable label for the device, used in filenames and the
 *  on-screen heading. e.g. "iPhone-Safari" / "Android-Chrome". */
export function shortDeviceLabel(fp: DeviceFingerprint): string {
  const ua = fp.userAgent;
  let os = "Unknown";
  if (/iPhone|iPad|iPod/.test(ua)) os = "iOS";
  else if (/Android/.test(ua)) os = "Android";
  else if (/Mac OS X/.test(ua)) os = "macOS";
  else if (/Windows/.test(ua)) os = "Windows";
  else if (/Linux/.test(ua)) os = "Linux";

  let browser = "Unknown";
  if (/Edg\//.test(ua)) browser = "Edge";
  else if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) browser = "Chrome";
  else if (/Firefox\//.test(ua)) browser = "Firefox";
  else if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) browser = "Safari";

  return `${os}-${browser}`;
}
